import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'src/utils/axios';
import { useDispatch } from 'react-redux';
import { addNotification } from 'src/actions/notificationActions';
import { NOTIFICATION_STATUS } from 'src/constants';

// The facts the escalation agent found while investigating this consultant's
// account. INTERNAL - the customer never sees it.
//
// Read-only by construction, and deliberately NOT part of the compose box.
// The note arrives as its own API field rather than through
// contactChatPreferences (which is the compose-prefill channel), so there is
// no path by which it can be sent: it cannot be edited, and nothing here
// writes to the message body.
//
// Collapsed by default. The draft is the thing being reviewed; the working is
// there when it is wanted and out of the way when it is not.
function AiResearchNote({
  contactId = null,
  onDraftApplied = () => {},
  refreshKey = '',
  onStateChanged = () => {},
}) {
  const dispatch = useDispatch();
  const [research, setResearch] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isFullOpen, setIsFullOpen] = useState(false);
  // Whether the note is taller than the panel, and whether she has reached the
  // end of it. Both are measured from the DOM rather than guessed from the
  // text length, because wrapping decides the real height.
  const [overflows, setOverflows] = useState(false);
  const [atEnd, setAtEnd] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!contactId) { setResearch(null); return; }
    try {
      const { data } = await axios.get('/api/ai-support-drafts/research', { params: { contactId } });
      setResearch(data?.enabled ? data.research : null);
    } catch (error) {
      setResearch(null);
    }
  }, [contactId]);

  // refreshKey is bumped by the restore control in the toolbar group. Without
  // it the two views of the same note drift apart: bring a note back and this
  // panel would not reappear until the page reloaded.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Expanded/collapsed comes from the draft row now. It used to be reset to
  // collapsed on every contact change, so a note she had deliberately left
  // open closed itself each time she came back to the conversation.
  useEffect(() => {
    setIsOpen(Boolean(research?.expanded));
  }, [research?.id, research?.expanded]);

  // Poll only while an investigation is actually running. It finishes in about
  // 20 seconds, so this stops on its own rather than running all day.
  useEffect(() => {
    if (research?.status !== 'pending') return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [research?.status, load]);

  // Measure whether the note is clipped, and re-measure on open and on any
  // note change. Without this the panel looks identical whether it ends at the
  // fold or continues below it - which is exactly how a reviewer comes to
  // judge a note she has only half read.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) { setOverflows(false); setAtEnd(false); return; }
    const clipped = el.scrollHeight > el.clientHeight + 1;
    setOverflows(clipped);
    setAtEnd(!clipped);
  }, [isOpen, research?.note]);

  const handleNoteScroll = (event) => {
    const el = event.currentTarget;
    setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
  };

  // Fire-and-forget: the UI has already moved, and a failed state write is
  // not worth interrupting her for. Worst case the note opens collapsed next
  // time, which is where it started.
  const persistState = useCallback(async (changes) => {
    if (!research?.id || !contactId) return;
    try {
      await axios.put('/api/ai-support-drafts/research/state', {
        contactId,
        aiSupportDraftId: research.id,
        ...changes,
      });
    } catch (error) {
      // Deliberately silent - see above.
    }
  }, [contactId, research?.id]);

  const toggleOpen = () => {
    setIsOpen((wasOpen) => {
      persistState({ expanded: !wasOpen });
      return !wasOpen;
    });
  };

  // No confirmation, on purpose. Nothing is destroyed - the note text is
  // untouched and the retrieval icon brings it straight back - and a confirm
  // on an action she performs many times a day is friction, not safety.
  const dismiss = async (event) => {
    event.stopPropagation();
    setResearch((prev) => (prev ? { ...prev, dismissedAt: new Date().toISOString() } : prev));
    // AWAIT before signalling. The signal makes both views refetch, and if the
    // write has not landed yet they read the pre-dismiss state back and the
    // panel reappears while the retrieval icon is also showing - the note
    // visible in two places at once.
    await persistState({ dismissed: true });
    // Tell the toolbar so the retrieval icon appears immediately. It holds its
    // own copy of the note and would otherwise not know until a reload, which
    // would mean dismissing had, for a while, no way back.
    onStateChanged();
  };

  // Accepting is an explicit act, never automatic. A silent replace would
  // destroy the only per-case signal we have about whether the revision was
  // actually better - and text changing under her cursor while she reads is
  // its own problem, which the draftSource guard cannot prevent.
  const applyRevision = async () => {
    setIsApplying(true);
    try {
      const { data } = await axios.post('/api/ai-support-drafts/research/accept', { contactId });
      if (data?.applied) {
        onDraftApplied(data.draftMessage);
        dispatch(addNotification('ai draft', 'Updated draft applied', NOTIFICATION_STATUS.SUCCESS));
      } else {
        // Never a silent no-op: if the box moved on she needs to know why
        // nothing happened.
        dispatch(addNotification(
          'ai draft',
          data?.reason || 'The draft has changed since this was written',
          NOTIFICATION_STATUS.WARNING,
        ));
      }
      await load();
    } catch (error) {
      dispatch(addNotification('ai draft', 'Could not apply the updated draft', NOTIFICATION_STATUS.ERROR));
    } finally {
      setIsApplying(false);
    }
  };

  if (!research) return null;

  const isPending = research.status === 'pending';
  const isLowConfidence = research.status === 'low-confidence';

  // The base draft was withheld ON PURPOSE for this question type (the
  // Table-join diagnosis path) and the note is the answer. The compose box is
  // empty by design, so without saying so the screen reads as "nothing was
  // produced": on 15 Sep the support person refreshed and waited six minutes
  // for a draft that was never coming.
  const isWithheld = Boolean(research.withheld);

  // She pressed "Look it up" for this one. A run she asked for must never
  // end in silence: if it produced no note (the model judged it not a lookup
  // question, or the run capped or failed), the bar stays and says so, so
  // the chip reading "Look it up again" is not the only evidence it ran.
  const isOnDemand = research.reason === 'on-demand';

  // Nothing worth showing for a failed or capped run: the base draft stands on
  // its own and an empty panel is just noise. Except when the draft was
  // withheld - then there is no base draft to stand on, and the one thing the
  // screen must say is that the empty box is deliberate.
  if (!isPending && !research.note && !isWithheld && !isOnDemand) return null;

  // Dismissed: the bar goes, the note does not. AiResearchNoteRestore puts a
  // retrieval icon in the toolbar group so it is one click away.
  if (research.dismissedAt) return null;

  // Extent, shown on the collapsed header so she knows what she is committing
  // to read before she opens it. Counted on the source text rather than the
  // rendered height so it is stable across window sizes.
  const noteLineCount = String(research.note || '')
    .split('\n')
    .filter((line) => line.trim()).length;

  return (
    <Box
      sx={{
        width: '100%',
        marginBottom: '8px',
        padding: '6px 10px',
        borderRadius: '6px',
        border: '1px solid',
        borderColor: isLowConfidence ? 'warning.light' : 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: '6px', cursor: isPending ? 'default' : 'pointer',
        }}
        onClick={() => !isPending && toggleOpen()}
      >
        <ScienceOutlinedIcon fontSize="small" color={isLowConfidence ? 'warning' : 'secondary'} />
        <Typography variant="caption" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {isPending
            ? (isWithheld ? 'Looking into this account - no draft on purpose...' : 'Looking into this account...')
            : 'Research note (internal)'}
          {!isPending && !!noteLineCount && (
            <Typography
              component="span"
              variant="caption"
              color="textSecondary"
              sx={{ fontWeight: 400, marginLeft: '6px' }}
            >
              {`- ${noteLineCount} line${noteLineCount === 1 ? '' : 's'}`}
            </Typography>
          )}
        </Typography>
        {isLowConfidence && (
          <Chip size="small" color="warning" variant="outlined" label="unverified" />
        )}
        {!isPending && (
          <IconButton size="small" aria-label={isOpen ? 'Hide research note' : 'Show research note'}>
            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
        {!isPending && (
          <TooltipMUI6 title="Dismiss - you can bring it back from the toolbar">
            <IconButton size="small" aria-label="Dismiss research note" onClick={dismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </TooltipMUI6>
        )}
      </Box>

      {isWithheld && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            marginTop: '4px',
            padding: '4px 6px',
            borderRadius: '4px',
            backgroundColor: 'info.light',
            color: 'info.contrastText',
          }}
        >
          {isPending
            ? 'No draft was written for this one on purpose: it reads as a Table-join question, and the account lookup is what answers it. The note lands here when it finishes.'
            : research.note
              ? 'No draft was written for this one on purpose: it reads as a Table-join question, and this note is the answer. Use it puts the updated draft in the box.'
              : 'No draft was written for this one on purpose: it reads as a Table-join question, but the account lookup did not finish, so there is no note. Answer it by hand.'}
        </Typography>
      )}

      {isOnDemand && !isPending && !research.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: '4px' }}>
          {research.status === 'not-a-lookup'
            ? 'You asked for a lookup, and the investigation decided this question is not one an account lookup can settle - nothing was written.'
            : `You asked for a lookup, and it finished without a note (${research.status}). You can run it again from the Look it up chip.`}
        </Typography>
      )}

      {!isPending && !!research.revisedDraftText && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '6px',
          flexWrap: 'wrap',
        }}
        >
          <Typography variant="caption" sx={{ flexGrow: 1, minWidth: '180px' }}>
            An updated draft is available based on these facts.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={isApplying}
            onClick={applyRevision}
            sx={{ flexShrink: 0 }}
          >
            Use it
          </Button>
        </Box>
      )}

      <Collapse in={isOpen && !isPending}>
        {/* The clipped note. Everything here exists to answer one question the
            old panel could not: is there more below? A note that ends at the
            fold on a full stop is indistinguishable from one that is finished,
            and a reviewer who judges on the visible half files feedback that is
            wrong about work the model actually did. */}
        <Box sx={{ position: 'relative', marginTop: '6px' }}>
          {/* CON-1673 — this note was written while the escalation was scoped
              to CP's own support account instead of the consultant's, so every
              account fact in it describes the wrong account. Kept rather than
              deleted, because the text is the record of how that was found —
              which is exactly why it has to say so on the face of it. */}
          {research.accountScopeUnreliable && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                marginBottom: '6px',
                padding: '4px 6px',
                borderRadius: '4px',
                backgroundColor: 'warning.light',
                color: 'warning.contrastText',
                fontWeight: 500,
              }}
            >
              Do not rely on the account details below. This note was written
              before 7 Sep 2026, when the investigation looked at CP&apos;s own
              support account instead of this consultant&apos;s — so anything it
              says about their groups, events, messages or billing is about the
              wrong account. The reasoning is still readable; the facts are not.
            </Typography>
          )}
          <Typography
            ref={scrollRef}
            onScroll={handleNoteScroll}
            variant="body2"
            component="div"
            sx={{
              whiteSpace: 'pre-wrap',
              maxHeight: '220px',
              overflowY: 'auto',
              // A scrollbar that is there BEFORE she scrolls. The platform
              // overlay scrollbar only appears once scrolling starts, so it
              // confirms what she already did instead of telling her what she
              // could do.
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '10px' },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'action.hover',
                borderRadius: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'text.disabled',
                borderRadius: '5px',
                border: '2px solid transparent',
                backgroundClip: 'content-box',
              },
            }}
          >
            {research.note}
          </Typography>
          {/* Fade only while there is something below. It clears as she
              reaches the end, so its absence is also information. */}
          {overflows && !atEnd && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                right: '10px',
                bottom: 0,
                height: '36px',
                pointerEvents: 'none',
                background: (theme) => `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
              }}
            />
          )}
        </Box>
        {overflows && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '2px',
          }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
              {atEnd ? 'End of note.' : 'This note continues below.'}
            </Typography>
            {/* Opens the whole note in a dialog rather than growing the panel.
                Growing it further would cover more of the conversation she is
                replying to, which is the opposite of helpful. */}
            <Button size="small" onClick={() => setIsFullOpen(true)} sx={{ flexShrink: 0 }}>
              Show all
            </Button>
          </Box>
        )}
        {isLowConfidence && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', marginTop: '4px' }}>
            The investigation took an unusually long path, so treat these facts as unverified.
          </Typography>
        )}
      </Collapse>

      {/* The whole note, uncut. Nothing here is clipped, so there is no state
          in which she can be reading a truncated version without knowing. */}
      <ModalDialogMUI6
        isVisible={isFullOpen}
        title="Research note (internal)"
        closeText="Close"
        onClose={() => setIsFullOpen(false)}
        content={(
          <Box sx={{ minWidth: '320px', maxWidth: '640px' }}>
            {isLowConfidence && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', marginBottom: '8px' }}>
                The investigation took an unusually long path, so treat these facts as unverified.
              </Typography>
            )}
            {/* Same warning as the inline panel. Without it Show all becomes a
                way to read the note without the caveat, which is the one place
                someone settles in to read the whole thing. */}
            {research.accountScopeUnreliable && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  marginBottom: '8px',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  backgroundColor: 'warning.light',
                  color: 'warning.contrastText',
                  fontWeight: 500,
                }}
              >
                Do not rely on the account details below. This note was written
                before 7 Sep 2026, when the investigation looked at CP&apos;s own
                support account instead of this consultant&apos;s — so anything it
                says about their groups, events, messages or billing is about the
                wrong account. The reasoning is still readable; the facts are not.
              </Typography>
            )}
            <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
              {research.note}
            </Typography>
          </Box>
        )}
      />
    </Box>
  );
}

AiResearchNote.propTypes = {
  contactId: PropTypes.number,
  // Lets the compose box pick up the applied text without a page refresh.
  onDraftApplied: PropTypes.func,
  // Bumped when the toolbar's restore control changes the note's state.
  // A composite string, not a counter: the two signals behind it move
  // independently and summing them can repeat a value.
  refreshKey: PropTypes.string,
  // Called when this panel changes the note's state, so the toolbar catches up.
  onStateChanged: PropTypes.func,
};

export default AiResearchNote;
